/**
 * InputToolbar — the bottom row of the chat input area.
 *
 * Composes the left-side action cluster (Paperclip + WebSearchToggle
 * pills + Activity), a visual separator, an inline AttachmentChipStrip
 * when files are present and not in wrap-above mode, the char-count
 * indicator when near the limit, and the send Button.
 *
 * Pure presentational — receives state slices and callbacks via props.
 */

import { Paperclip, Activity, Send, Square } from 'lucide-react';
import { cn } from '@/utils/classNames';
import { Button } from '@/components/Common';
import { IconButton } from './IconButton';
import { WebSearchToggle } from './WebSearchToggle';
import { ReasoningToggle } from './ReasoningToggle';
import {
  AttachmentChipStrip,
  type ChipStripMode,
} from './AttachmentChipStrip';
import type { Agent } from '@/types/agent.types';

// Pill styling lifted from WebSearchToggle so the Tokens toggle visually
// groups with the Web pill instead of looking like an orphaned icon
// next to a labeled pill. Kept inline rather than extracted into a
// shared util — only two call sites and the duplication is small.
const TOGGLE_PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ' +
  'font-medium transition-colors focus:outline-none ' +
  'focus:ring-2 focus:ring-stellar-primary disabled:opacity-50 ' +
  'disabled:cursor-not-allowed';
const TOGGLE_PILL_ON = 'bg-stellar-primary text-white';
const TOGGLE_PILL_OFF =
  'border border-border-default text-text-secondary hover:bg-bg-secondary';

export interface InputToolbarProps {
  canAttach: boolean;
  onAttachClick: () => void;
  selectedAgent: Agent | undefined;
  showTokenUsage: boolean;
  onToggleTokenUsage: () => void;
  files: File[];
  onRemoveFile: (index: number) => void;
  chipStripMode: ChipStripMode;
  onChipStripModeChange: (mode: ChipStripMode) => void;
  charCount: number;
  charLimit: number;
  disabled: boolean;
  canSend: boolean;
  onSend: () => void;
  /**
   * True while a chat completion is in flight. Replaces the Send icon
   * with a Stop icon and wires the button to `onCancel`. Long Model
   * Router calls used to look identical to a hang — this is the user's
   * way out.
   */
  isLoading?: boolean;
  onCancel?: () => void;
}

export function InputToolbar({
  canAttach,
  onAttachClick,
  selectedAgent,
  showTokenUsage,
  onToggleTokenUsage,
  files,
  onRemoveFile,
  chipStripMode,
  onChipStripModeChange,
  charCount,
  charLimit,
  disabled,
  canSend,
  onSend,
  isLoading = false,
  onCancel,
}: InputToolbarProps) {
  const isNearLimit = charCount > charLimit * 0.9;
  const isAtLimit = charCount >= charLimit;

  return (
    // Compact row (py-1.5) — the 32px buttons used to float in a 52px row,
    // wasting vertical space in the input box. The Send/Stop buttons drop the
    // ring-offset (focus-visible:!ring-offset-0 below) so their focus ring hugs
    // the button and stays inside the rounded `overflow-hidden` container border
    // instead of being clipped by it; the IconButtons/pills already ring without
    // an offset. overflow-hidden stays on the parent so the textarea scrollbar
    // is clipped to the rounded top corners on long messages.
    <div className="flex items-center gap-2 px-3 py-0.5">
      {/* Action cluster */}
      {canAttach && (
        <IconButton
          icon={<Paperclip className="h-[18px] w-[18px]" />}
          label="Attach file"
          onClick={onAttachClick}
          disabled={disabled}
        />
      )}

      <WebSearchToggle agent={selectedAgent} />

      <ReasoningToggle agent={selectedAgent} />

      <button
        type="button"
        aria-pressed={showTokenUsage}
        aria-label={
          showTokenUsage ? 'Hide token usage' : 'Show token usage'
        }
        title={showTokenUsage ? 'Hide token usage' : 'Show token usage'}
        onClick={onToggleTokenUsage}
        disabled={disabled}
        className={cn(
          TOGGLE_PILL_BASE,
          showTokenUsage ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF
        )}
      >
        <Activity className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Tokens</span>
      </button>

      {/* Separator */}
      <div
        className="mx-1 h-5 w-px flex-shrink-0 bg-border-default"
        aria-hidden="true"
      />

      {/* Inline chips (only when not overflowing) */}
      {chipStripMode === 'inline' && (
        <AttachmentChipStrip
          files={files}
          onRemove={onRemoveFile}
          mode="inline"
          onModeChange={onChipStripModeChange}
        />
      )}

      {/* Spacer */}
      <div className="ml-auto" />

      {/* Char count */}
      {isNearLimit && (
        <span
          className={cn(
            'flex-shrink-0 text-xs font-medium',
            isAtLimit ? 'text-error' : 'text-warning'
          )}
        >
          {charCount}/{charLimit}
        </span>
      )}

      {/* Send / Stop — flips role while a chat completion is in flight
          so the same button slot doubles as a cancel control. */}
      {isLoading && onCancel ? (
        <Button
          onClick={onCancel}
          variant="primary"
          size="sm"
          className="flex-shrink-0 focus-visible:!ring-offset-0"
          title="Stop generating"
          aria-label="Stop generating"
        >
          <Square className="h-[18px] w-[18px]" fill="currentColor" />
        </Button>
      ) : (
        <Button
          onClick={onSend}
          disabled={!canSend}
          variant="primary"
          size="sm"
          className="flex-shrink-0 focus-visible:!ring-offset-0"
          title="Send message (Enter)"
          aria-label="Send message"
        >
          <Send className="h-[18px] w-[18px]" />
        </Button>
      )}
    </div>
  );
}
